import { useState } from "react";
import { BellOff, ChevronDown, ChevronUp, Pause, Play, Plus, VolumeX, X } from "lucide-react";
import { useTimers, getRemainingSeconds } from "@/hooks/useTimers";
import { formatRemaining } from "@/lib/timeParse";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * Floating dock that lists every active/finished timer. Mounted globally; only
 * renders when ≥1 timer exists, so it stays invisible for free users who never
 * start one.
 */
export const TimerDock = () => {
  const { timers, pause, resume, addSeconds, dismiss, alarmActive, alarmMuted, stopAlarm } = useTimers();
  const [collapsed, setCollapsed] = useState(false);

  if (timers.length === 0) return null;

  // Sort: finished first (need attention), then running by soonest, then paused.
  const sorted = [...timers].sort((a, b) => {
    const order = (s: typeof a.status) =>
      s === "finished" ? 0 : s === "running" ? 1 : 2;
    const oa = order(a.status);
    const ob = order(b.status);
    if (oa !== ob) return oa - ob;
    return a.endsAt - b.endsAt;
  });

  const headline = sorted[0];
  const headlineRemaining = getRemainingSeconds(headline);

  return (
    <div
      className="fixed left-1/2 z-40 w-[min(22rem,calc(100vw-1rem))] -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
      role="region"
      aria-label="Active cooking timers"
    >
      <div
        className={cn(
          "overflow-hidden rounded-2xl border bg-background/95 shadow-soft backdrop-blur transition-colors",
          alarmActive
            ? "border-destructive/60 ring-2 ring-destructive/40 animate-pulse"
            : "border-border",
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
          aria-expanded={!collapsed}
        >
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                headline.status === "finished"
                  ? "animate-pulse bg-destructive/15 text-destructive"
                  : "bg-primary/15 text-primary",
              )}
            >
              {timers.length}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {headline.status === "finished"
                  ? "Timer finished"
                  : formatRemaining(headlineRemaining)}
              </p>
              <p className="truncate text-xs text-muted-foreground">{headline.label}</p>
            </div>
          </div>
          {collapsed ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {alarmActive && (
          <div className="flex items-center gap-2 border-t border-border bg-destructive/10 px-3 py-2">
            {alarmMuted ? (
              <>
                <VolumeX className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                <p className="flex-1 text-xs text-foreground">
                  Alarm sound is blocked. Check your volume or tap to enable sound.
                </p>
              </>
            ) : (
              <p className="flex-1 text-xs font-medium text-destructive">
                Alarm ringing — dismiss to silence
              </p>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 border-destructive/40 px-2 text-xs"
              onClick={stopAlarm}
            >
              <BellOff className="h-3.5 w-3.5" />
              Stop alarm
            </Button>
          </div>
        )}

        {!collapsed && (
          <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">
            {sorted.map((t) => {
              const remaining = getRemainingSeconds(t);
              const isFinished = t.status === "finished";
              const isRunning = t.status === "running";
              return (
                <li key={t.id} className="flex items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        "truncate text-sm font-medium",
                        isFinished ? "text-destructive" : "text-foreground",
                      )}
                    >
                      {t.label}
                    </p>
                    {t.recipeTitle && (
                      <p className="truncate text-[11px] text-muted-foreground">
                        {t.recipeTitle}
                      </p>
                    )}
                  </div>
                  <span
                    className={cn(
                      "tabular-nums font-display text-base font-semibold",
                      isFinished
                        ? "text-destructive"
                        : isRunning
                          ? "text-primary"
                          : "text-muted-foreground",
                    )}
                  >
                    {isFinished ? "00:00" : formatRemaining(remaining)}
                  </span>
                  {!isFinished && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => (isRunning ? pause(t.id) : resume(t.id))}
                      aria-label={isRunning ? "Pause timer" : "Resume timer"}
                    >
                      {isRunning ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => addSeconds(t.id, 60)}
                    aria-label="Add one minute"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => dismiss(t.id)}
                    aria-label="Dismiss timer"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
